import { companyRequest } from '@/company/api';
import type {
  ProjectDetail,
  ProjectDraft,
  ProjectItemKind,
  ProjectPerson,
  ProjectPlace,
  ProjectReadFirst,
  ProjectSummary,
  ProjectTint,
} from './types';

const root = '/api/v1/company/projects';
const segment = (id: string) => encodeURIComponent(id);

export interface ProjectEdit {
  name?: string;
  brief?: string;
  endsOn?: string | null;
  followsSkillIds?: string[];
  readsFirst?: { kind: 'file' | 'knowledge_page'; id: string }[];
  baseRevision?: number;
}

/** The draft canvas's Create: the project, its work and its people, private unless people were named. */
export function createInputFromDraft(draft: ProjectDraft) {
  const audience = draft.people.flatMap((person) =>
    person.userId ? [{ userId: person.userId, role: person.role }] : [],
  );
  return {
    name: draft.name,
    brief: draft.brief,
    ...(draft.endsOn ? { endsOn: draft.endsOn } : {}),
    ...(draft.tint ? { tint: draft.tint } : {}),
    followsSkillIds: draft.follows.map((skill) => skill.id),
    readsFirst: draft.readsFirst.map(({ kind, id }) => ({ kind, id })),
    items: draft.items.filter((item) => item.kind !== 'chat').map(({ kind, id }) => ({ kind, id })),
    ...(draft.chatId ? { chatId: draft.chatId } : {}),
    ...(audience.length ? { visibility: 'members' as const, audience } : {}),
  };
}

export const projectsApi = {
  list: () => companyRequest<{ projects: ProjectSummary[] }>(root).then((r) => r.projects),
  get: (id: string) =>
    companyRequest<{ project: ProjectDetail }>(`${root}/${segment(id)}`).then((r) => r.project),
  /** The draft's Create, or the New project form: a name, and maybe a brief, a colour and the chat to bring in. */
  create: (
    input:
      | ReturnType<typeof createInputFromDraft>
      | { name: string; brief?: string; tint?: ProjectTint; chatId?: string },
  ) =>
    companyRequest<{ project: ProjectDetail }>(root, 'POST', input).then((r) => r.project),
  update: (id: string, edit: ProjectEdit) =>
    companyRequest<{ project: ProjectDetail }>(`${root}/${segment(id)}`, 'PATCH', edit).then(
      (r) => r.project,
    ),
  remove: (id: string) => companyRequest<{ ok: true }>(`${root}/${segment(id)}`, 'DELETE'),
  addItem: (id: string, item: { kind: Exclude<ProjectItemKind, 'chat'>; id: string }) =>
    companyRequest<{ project: ProjectDetail }>(`${root}/${segment(id)}/items`, 'POST', item).then(
      (r) => r.project,
    ),
  /** null when the item's owner took it out without access to the project. */
  removeItem: (id: string, kind: ProjectItemKind, itemId: string) =>
    companyRequest<{ project: ProjectDetail | null }>(
      `${root}/${segment(id)}/items/${segment(kind)}/${segment(itemId)}`,
      'DELETE',
    ).then((r) => r.project),
  /** Moves a chat the reader owns into a project, or out of one with null. */
  moveChat: (threadId: string, projectId: string | null) =>
    companyRequest<{ threadId: string; project: ProjectPlace | null }>(
      `/api/v1/company/threads/${segment(threadId)}/project`,
      'PUT',
      { projectId },
    ),
  /** Who in the proposed audience can't open what every chat in the project reads first. */
  accessCheck: (id: string, audience: { userIds: string[] } | { company: true }) =>
    companyRequest<{ readsFirst: (ProjectReadFirst & { cannotOpen: ProjectPerson[] })[] }>(
      `${root}/${segment(id)}/access-check`,
      'POST',
      audience,
    ).then((r) => r.readsFirst),
};
